import { isGridFilterItemActive } from '@/apis/listGridFilters';
import { GetListParameters } from '@/apis/lists';
import { environment, microfrontendURLPrefix } from '@/config';
import { Supporter } from '@/types';
import { useAuth0 } from '@auth0/auth0-react';
import { usePrefetchUpcomingElection } from '@components/DistributionListPanel';
import { useUserDetails } from '@hooks/useUserDetails';
import { useSentryTags } from '@hooks/useSentryTags';
import { GridFilterModel, GridSortModel } from '@mui/x-data-grid';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useList, useListMetadata, useListStatistics } from './hooks';
import { PanelContent } from './ListDetailRightPanel';

const DEFAULT_PAGE_SIZE = 10;

export function useListDetailPage() {
  const { listId, tabSelection } = useParams();

  useSentryTags({ 'list-id': listId });

  const selectTabValue =
    tabSelection === 'dashboard' ? 'dashboard' : tabSelection === 'calling' ? 'calling' : 'detail';

  const { user } = useAuth0();
  const userSub = user?.sub ?? '';
  usePrefetchUpcomingElection();

  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [rightPanelOpen, setRightPanelOpen] = useState(false);
  const [rightPanelContent, setRightPanelContent] = useState<PanelContent>('assign');
  const [pageNo, setPageNo] = useState(0);

  const offset = pageNo * pageSize;

  const [filterOptions, setFilterOptions] = useState<GridFilterModel>({ items: [] });
  const filters = useMemo(
    () => filterOptions.items.filter(isGridFilterItemActive),
    [filterOptions.items]
  );

  const [callingViewContactIndex, setCallingViewContactIndex] = useState(-1);

  const [sortModel, setSortModel] = useState<GridSortModel>([
    {
      field: 'id',
      sort: 'asc',
    },
  ]);

  const [allContacts, setAllContacts] = useState(true);
  const [selectedContacts, setSelectedContacts] = useState<Supporter[]>([]);
  const [enterCallingByTableClick, setEnterCallingByTableClick] = useState(false);
  const [viewContactedChecked, setViewContactedChecked] = useState(false);

  const navigate = useNavigate();

  const { data: userDetails, isLoading: isLoadingUserDetails } = useUserDetails();

  const changeTab = useCallback(
    (destinationTab: string, replace = false) => {
      navigate(`/lists/${listId}/${destinationTab}`, { replace });
    },
    [listId, navigate]
  );

  const {
    data: listMetadata,
    isLoading,
    isSuccess: isListMetadataSuccess,
  } = useListMetadata(Number(listId));

  const { data: listStatistics, refetch: refetchListStatistics } = useListStatistics({
    listId: Number(listId),
    assignedToSub: userSub,
  });

  const isUserACoordinator =
    userDetails?.userLevel === 'Principal Coordinator' ||
    (listMetadata?.coordinators.some(({ id }) => id === userSub) ?? false);

  const isUserACaller = listMetadata?.assignees.some((x) => x.id === userSub) ?? false;

  const getAllContacts = allContacts && isUserACoordinator;

  const detailedQueryParams: Omit<GetListParameters, 'token' | 'domainCode'> = useMemo(
    () => ({
      listId: Number(listId),
      limit: pageSize,
      offset,
      sortColumn: sortModel[0].field === 'id' ? 'contactIdNumber' : sortModel[0].field,
      sortOrder: sortModel[0].sort ?? undefined,
      assignedToSub: getAllContacts ? '' : userSub,
      filters,
    }),
    [filters, getAllContacts, listId, offset, pageSize, sortModel, userSub]
  );

  const {
    data: listMembers,
    isLoading: isLoadingListMembers,
    isFetching: isFetchingListMembers,
    refetch: refetchMembers,
  } = useList(detailedQueryParams);

  useEffect(() => {
    const isDev = ['test', 'local', 'dev'].includes(environment);
    const intervalLength = isDev ? 10000 : 60000;
    const interval = setInterval(() => {
      refetchListStatistics();
    }, intervalLength);
    return () => clearInterval(interval);
  }, [refetchListStatistics, refetchMembers]);

  const queryClient = useQueryClient();

  useEffect(() => {
    if (
      !tabSelection ||
      tabSelection === '' ||
      (!isUserACoordinator && isUserACaller && tabSelection === 'dashboard')
    )
      changeTab('detail', true);
  }, [changeTab, isUserACaller, isUserACoordinator, tabSelection]);

  const handleTopValueChange = (newValue: string) => {
    changeTab(newValue);
    if (newValue === 'calling') {
      setCallingViewContactIndex(
        listMembers && listMembers.count && listMembers.firstUncalledIndex !== null
          ? (listMembers.firstUncalledIndex as number)
          : 0
      );
    }
  };

  const isAllContactsAssignedToUser =
    (listMetadata?.count ?? 0) > 0 &&
    listMetadata?.assignees.length === 1 &&
    listMetadata?.assignees[0].count === listMetadata?.count &&
    listMetadata?.assignees[0].id === userSub;

  const contactsIdsAssignedList = listMetadata?.assignees.map((x) => x.id);
  const noContactsAssignedToUser =
    listMetadata?.count === 0 ||
    listMetadata?.assignees.length === 0 ||
    contactsIdsAssignedList?.indexOf(userSub) === -1;

  const disableToggle =
    isAllContactsAssignedToUser ||
    noContactsAssignedToUser ||
    rightPanelOpen ||
    !isUserACoordinator;

  /* eslint-disable react-you-might-not-need-an-effect/no-event-handler, react-you-might-not-need-an-effect/no-chain-state-updates -- force All contacts when the Me toggle is disabled */
  useEffect(() => {
    if (disableToggle && !allContacts) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- force All contacts when the Me toggle is disabled
      setAllContacts(true);
    }
  }, [disableToggle, allContacts, setAllContacts]);

  const handleAllContactsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setAllContacts(event.target.checked);
    setPageNo(0);
    setCallingViewContactIndex(-1);
    queryClient.invalidateQueries({ queryKey: ['list', listId] });
  };

  const onSortModelChange = (model: GridSortModel) => {
    if (model.length === 0) {
      setSortModel([{ field: 'id', sort: 'asc' }]);
      setPageNo(0);
      setCallingViewContactIndex(-1);
      queryClient.invalidateQueries({ queryKey: ['list', listId] });
    } else if (model[0].field !== sortModel[0].field || model[0].sort !== sortModel[0].sort) {
      setSortModel(model);
      setPageNo(0);
      setCallingViewContactIndex(-1);
      queryClient.invalidateQueries({ queryKey: ['list', listId] });
    }
  };

  const handleOpenRightPanel = (content: PanelContent) => {
    setRightPanelOpen(true);
    setRightPanelContent(content);
  };

  const handleAssign = (destinationTab: string) => {
    if (destinationTab === 'lists') {
      navigate(`${microfrontendURLPrefix}/lists`);
      return;
    }
    setRightPanelOpen(false);
    changeTab(destinationTab);
  };

  const handleOtherActionsClose = async () => {
    setRightPanelOpen(false);
  };

  /* eslint-disable react-you-might-not-need-an-effect/no-event-handler, react-you-might-not-need-an-effect/no-chain-state-updates -- jump to the first uncalled contact on load */
  useEffect(() => {
    if (
      listMembers &&
      callingViewContactIndex === -1 &&
      listMembers.count &&
      !isFetchingListMembers &&
      !isLoadingListMembers
    ) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- jump to the first uncalled contact on load
      setCallingViewContactIndex(
        listMembers.firstUncalledIndex !== null ? (listMembers.firstUncalledIndex as number) : 0
      );
    }
  }, [
    listMembers,
    callingViewContactIndex,
    setCallingViewContactIndex,
    isFetchingListMembers,
    isLoadingListMembers,
  ]);

  useEffect(() => {
    if (selectTabValue !== 'calling') return;
    if (
      listMembers &&
      listMembers.count &&
      listMembers.limit &&
      callingViewContactIndex >= 0
    ) {
      const expectedPageNo = Math.floor(callingViewContactIndex / listMembers.limit);
      if (expectedPageNo !== pageNo) {
        // eslint-disable-next-line react-hooks/set-state-in-effect -- keep the members page in sync with calling index
        setPageNo(expectedPageNo);
      }
    }
  }, [listMembers, callingViewContactIndex, pageNo, selectTabValue, setPageNo]);

  useEffect(() => {
    if (!isLoading && selectTabValue === 'dashboard' && !isUserACoordinator) {
      changeTab('detail');
    }
  }, [changeTab, isLoading, isUserACoordinator, selectTabValue]);

  // Only treat as not-found after metadata successfully loaded and proves no access.
  // Failed/empty fetches must not look like a missing page (e.g. brief outages).
  const showNotFound =
    isListMetadataSuccess &&
    !!listMetadata &&
    !isLoadingUserDetails &&
    !isUserACaller &&
    !isUserACoordinator;

  return {
    listId,
    selectTabValue,
    showNotFound,
    rightPanelOpen,
    rightPanelContent,
    setRightPanelOpen,
    handleTopValueChange,
    allContacts,
    disableToggle,
    handleAllContactsChange,
    listMetadata,
    listStatistics,
    isLoading,
    isUserACoordinator,
    isUserACaller,
    viewContactedChecked,
    setViewContactedChecked,
    pageNo,
    setPageNo,
    setFilterOptions,
    pageSize,
    setPageSize,
    setCallingViewContactIndex,
    changeTab,
    onSortModelChange,
    sortModel,
    handleOpenRightPanel,
    listMembers,
    isLoadingListMembers,
    selectedContacts,
    setSelectedContacts,
    setEnterCallingByTableClick,
    detailedQueryParams,
    callingViewContactIndex,
    enterCallingByTableClick,
    // Initial load only — do not treat background refetch (e.g. outage recovery
    // invalidate) as a full-page/table loading state; that flickers existing rows.
    listsAreLoadingAndOrFetching: isLoading || isLoadingListMembers,
    handleAssign,
    handleOtherActionsClose,
  };
}
